const CONSTANTS = {
  SECTION: 'checkout',
  PAGE_CATEGORY: 'ecommerce',
  PAGE_TEMPLATE: 'auth-page',
  PAGE_BUILD: 'react',
  LOGIN_STATUS: 'logged out',
  LOGIN_TYPE: 'account',
  FORM_CATEGORY: 'user account',
  COMPONENT_TYPE_BUTTON: 'button',
  COMPONENT_TYPE_LINK: 'link',
  CLICK_CATEGORY_CTA: 'cta',
  CLICK_CATEGORY_NAV: 'navigation',
  EMPTY: '',
} as const;

export interface AdobeDataLayerEvent {
  event: string;
  page?: {
    pageName: string;
    section: string;
    subSection: string;
    subSubSection: string;
    pageCategory: string;
    pageLanguage: string;
    pageTemplate: string;
    pageBuild: string;
    pageNumber: string;
    breadcrumb: string;
  };
  site?: {
    country: string;
    locale: string;
    platform: string;
    name: string;
  };
  user?: {
    loginStatus: string;
    loginType: string;
    adminType: string;
    almId: string;
    connectId: string;
  };
  component?: {
    componentType: string;
    componentText: string;
    componentProductCode: string;
    componentPosition: string;
    linkURL: string;
    clickCategory: string;
    parentComponentType: string;
    parentComponentText: string;
  };
  form?: {
    formName: string;
    formCategory: string;
  };
  error?: {
    serverErrorCode: string;
    errorMessage: string;
  };
}

const detectPlatform = (): 'web' | 'ios' | 'android' => {
  const userAgent = window?.navigator?.userAgent?.toLowerCase() || '';
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }
  if (/android/.test(userAgent)) {
    return 'android';
  }
  return 'web';
};

const parseLocale = (locale: string) => {
  const parts = locale.toLowerCase().split('-');
  if (parts.length === 2) {
    return { country: parts[1], language: parts[0] };
  }
  return { country: locale.toLowerCase(), language: 'en' };
};

const buildPageViewEvent = (subSection: string, locale: string) => {
  const { country, language } = parseLocale(locale);
  const localeStr = `${country}-${language}`;
  const { hostname } = window.location;
  const pageNameMap: Record<string, string> = {
    'create account': 'create-account',
    'sign in': 'login',
  };
  const pageName = pageNameMap[subSection] || subSection;

  return {
    event: 'pageView',
    page: {
      pageName: pageName.toLowerCase(),
      section: CONSTANTS.SECTION,
      subSection,
      subSubSection: CONSTANTS.EMPTY,
      pageCategory: CONSTANTS.PAGE_CATEGORY,
      pageLanguage: language.toLowerCase(),
      pageTemplate: CONSTANTS.PAGE_TEMPLATE,
      pageBuild: CONSTANTS.PAGE_BUILD,
      pageNumber: CONSTANTS.EMPTY,
      breadcrumb: CONSTANTS.EMPTY,
    },
    site: {
      country: country.toLowerCase(),
      locale: localeStr.toLowerCase(),
      platform: detectPlatform(),
      name: hostname,
    },
    user: {
      loginStatus: CONSTANTS.LOGIN_STATUS,
      loginType: CONSTANTS.LOGIN_TYPE,
      adminType: CONSTANTS.EMPTY,
      almId: CONSTANTS.EMPTY,
      connectId: CONSTANTS.EMPTY,
    },
  };
};

const buildFormComponent = (formName: string) => ({
  componentType: CONSTANTS.COMPONENT_TYPE_BUTTON,
  componentText: formName,
  componentProductCode: CONSTANTS.EMPTY,
  componentPosition: CONSTANTS.EMPTY,
  linkURL: CONSTANTS.EMPTY,
  clickCategory: CONSTANTS.CLICK_CATEGORY_CTA,
  parentComponentType: CONSTANTS.EMPTY,
  parentComponentText: CONSTANTS.EMPTY,
});

const buildFormEvent = (
  event: string,
  formName: string,
  error?: { serverErrorCode: string; errorMessage: string },
) => {
  const baseEvent = {
    event,
    form: {
      formName,
      formCategory: CONSTANTS.FORM_CATEGORY,
    },
    component: buildFormComponent(formName),
  };

  return error ? { ...baseEvent, error } : baseEvent;
};

export const onRegistrationPageLoad = (locale: string) => {
  const { length } = window.adobeDataLayer;
  const eventData = buildPageViewEvent('create account', locale);
  const { pageName } = eventData.page;

  if (length === 0 || window.adobeDataLayer[length - 1]?.page?.pageName !== pageName) {
    window.adobeDataLayer.push(eventData);
  }
};

export const onSignInPageLoad = (locale: string) => {
  const { length } = window.adobeDataLayer;
  const eventData = buildPageViewEvent('login', locale);
  const { pageName } = eventData.page;

  if (length === 0 || window.adobeDataLayer[length - 1]?.page?.pageName !== pageName) {
    window.adobeDataLayer.push(eventData);
  }
};

export const onContinueAsGuestClick = (btnTitle: string) => {
  window.adobeDataLayer.push({
    event: 'click',
    component: {
      componentType: CONSTANTS.COMPONENT_TYPE_BUTTON,
      componentText: (btnTitle || CONSTANTS.EMPTY).toLowerCase(),
      componentProductCode: CONSTANTS.EMPTY,
      componentPosition: CONSTANTS.EMPTY,
      linkURL: CONSTANTS.EMPTY,
      clickCategory: CONSTANTS.CLICK_CATEGORY_CTA,
      parentComponentType: 'form',
      parentComponentText: 'guest checkout',
    },
  });
};

export const onCreateAccountSuccess = () => {
  window.adobeDataLayer.push(buildFormEvent('createAccountSuccess', 'create account'));
};

export const onCreateAccountFailure = (errorCode: string, errorMessage: string) => {
  window.adobeDataLayer.push(
    buildFormEvent('createAccountFailure', 'create account', {
      serverErrorCode: errorCode || 'unknown',
      errorMessage: errorMessage || 'An error occurred',
    }),
  );
};

export const onSignInSuccess = () => {
  window.adobeDataLayer.push(buildFormEvent('signInSuccess', 'sign in'));
};

export const onSignInFailure = (errorCode: string, errorMessage: string) => {
  window.adobeDataLayer.push(
    buildFormEvent('signInFailure', 'sign in', {
      serverErrorCode: errorCode || 'unknown',
      errorMessage: errorMessage || 'An error occurred',
    }),
  );
};
